import {computed, ref, watch} from 'vue';

import type {IRecipient} from '@/api/types/recipient';
import type {IUser} from '@/api/types/user';

import {recipientAPI} from '@/api/requesters/recipient';
import {userAPI} from '@/api/requesters/user';
import {keycloakAPI} from '@/assets/keycloak';

import {useRecipientPresenterHook} from '@/services/presenters/recipient';
import {useUserPresenterHook} from '@/services/presenters/user';

const useUserStateHook = () => {
  const userInfo = ref<IUser | undefined>(undefined);
  const userRecipientInfo = ref<IRecipient | undefined>(undefined);

  const getInfo = computed<IUser>(() => ({
    id: userInfo.value?.id ?? keycloakAPI.getUserInfo.value.id ?? '',

    email: userInfo.value?.email ?? keycloakAPI.getUserInfo.value.email,
    name: userInfo.value?.name ?? keycloakAPI.getUserInfo.value.first_name,
    username: userInfo.value?.username ?? null,

    roles: [
      ...new Set(
        (userInfo.value?.roles ?? []).concat(
          keycloakAPI.getUserInfo.value.roles,
        ),
      ),
    ],
    tech_roles: userInfo.value?.tech_roles ?? [],
    view_roles: userInfo.value?.view_roles ?? [],
    view_roles_map: userInfo.value?.view_roles_map ?? [],
  }));

  const getRecipientInfo = computed(() => userRecipientInfo.value);

  const getPresenter = computed(() => useUserPresenterHook(getInfo));

  const getRecipientPresenter = computed(() =>
    useRecipientPresenterHook(getRecipientInfo.value),
  );

  const getId = computed(() => getInfo.value.id);

  const getRecipientId = computed(() => getRecipientInfo.value?.id ?? '');

  const loadUserInfo = async (payload?: IUser) => {
    userInfo.value =
      payload ?? (getId.value ? await userAPI.getMe() : undefined);
    userRecipientInfo.value = getId.value
      ? await recipientAPI.getMe()
      : undefined;
    return userInfo.value;
  };

  watch(getId, () => {
    loadUserInfo();
  });

  return {
    getId,
    getInfo,

    ...getPresenter.value,

    recipient: {
      getRecipientId,
      getRecipientInfo,

      ...getRecipientPresenter.value,
    },

    loadUserInfo,

    getIsLoggedIn: keycloakAPI.getIsLoggedIn,
    signIn: keycloakAPI.signIn,
    signOut: keycloakAPI.signOut,
  };
};

export const userState = useUserStateHook();
